import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Modal, Nav } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCreditCard, FaPlus, FaTrashAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import axios from "axios";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Header from "../../components/Header";
import AppConstants from "../../AppConstants";
import useAuthToken from "../../hooks/useAuthToken";
import AddPaymentMethodForm from "./AddPaymentMethodForm";
import iconPayment from "../../images/icon-payment.png";
import "./style.scss";

// Initialize Stripe
const stripePromise = loadStripe(AppConstants.STRIPE_PUBLISHABLE_KEY);

interface PaymentMethod {
  _id: string;
  stripePaymentMethodId: string;
  type: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
  createdAt: string;
}

interface PaymentHistory {
  id: string;
  transactionId: string;
  date: string;
  amount: number;
  paymentMethod: string;
  maxUsers: number;
}

interface PlanData {
  customerId?: string;
  planName?: string;
  priceId?: string;
  maxUsers: number;
  promocode: string;
  autoRenew: boolean;
  totalPrice?: number;
}

// Function to fetch payment methods (Axios version)
const fetchPaymentMethods = async (
  setPaymentMethods: any,
  token: string | null
) => {
  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` })
      }
    };

    const response = await axios.get(
      AppConstants.PAYMENTS_LIST_METHODS_API,
      config
    );
    console.log("Payment Methods API Response:", response.data);

    if (response.data.success && response.data.data) {
      setPaymentMethods(response.data.data);
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    throw error;
  }
};

// Function to set default payment method (Axios version)
const setDefaultPaymentMethod = async (
  paymentMethodId: string,
  token: string | null
) => {
  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` })
      }
    };

    const data = { paymentMethodId };
    const response = await axios.put(
      AppConstants.PAYMENTS_SET_DEFAULT_METHOD_API,
      data,
      config
    );
    console.log("Set Default Payment Method API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error setting default payment method:", error);
    throw error;
  }
};

// Function to set default payment method (Fetch version - current)

const PaymentMethods: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userToken, ready, getUserIdFromToken } = useAuthToken();
  const [activeTab, setActiveTab] = useState<"methods" | "history">("methods");
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get plan data from navigation state
  const planData: PlanData = location.state || {
    maxUsers: 1,
    promocode: "",
    autoRenew: true
  };

  // Debug logs for plan data
  console.log("PaymentMethods - Location state:", location.state);

  // Helper function to format expiry date
  const formatExpiry = (expMonth: number, expYear: number) => {
    return `${expMonth.toString().padStart(2, "0")}/${expYear
      .toString()
      .slice(-2)}`;
  };

  // Helper function to capitalize brand name
  const capitalizeBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  // Call API on component mount to fetch payment methods
  useEffect(() => {
    const loadPaymentMethods = async () => {
      if (!ready) return;

      try {
        await fetchPaymentMethods(setPaymentMethods, userToken);
      } catch (error) {
        console.error("Failed to load payment methods:", error);
      }
    };

    loadPaymentMethods();
  }, [ready, userToken]); // Wait for auth hook to be ready

  // API now returns { payments: [...], ... } so store payments array only
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  // Fetch payment history when Payment History tab is active
  useEffect(() => {
    const fetchPaymentHistory = async () => {
      if (!ready || activeTab !== "history") return;
      try {
        // Get userId from token
        const userId = getUserIdFromToken();
        if (!userId) return;
        const config = {
          headers: {
            "Content-Type": "application/json",
            ...(userToken && { Authorization: `Bearer ${userToken}` })
          }
        };
        const url = `${AppConstants.ADMIN_USER_MANAGEMENT_API}/${userId}/payment-history`;
        const response = await axios.get(url, config);
        console.log("Payment History API Response:", response.data);
        if (
          response.data.success &&
          response.data.data &&
          response.data.data.payments
        ) {
          setPaymentHistory(response.data.data.payments);
        }
      } catch (error) {
        console.error("Error fetching payment history:", error);
      }
    };
    fetchPaymentHistory();
  }, [ready, activeTab, userToken]);

  const handleBackToSubscription = () => {
    navigate("/subscription");
  };

  const handleSetDefault = async (id: string) => {
    try {
      // Find the payment method to get the stripePaymentMethodId
      const selectedMethod = paymentMethods.find((method) => method._id === id);
      if (!selectedMethod) {
        console.error("Payment method not found");
        return;
      }

      // Call API to set default payment method
      await setDefaultPaymentMethod(
        selectedMethod.stripePaymentMethodId,
        userToken
      );

      // Update local state if API call is successful
      setPaymentMethods(
        paymentMethods.map((method) => ({
          ...method,
          isDefault: method._id === id
        }))
      );
    } catch (error) {
      console.error("Failed to set default payment method:", error);
      // You could show a toast notification here if needed
      // toast.error("Failed to set default payment method");
    }
  };

  // Convert PaymentMethod[] to the format expected by AddPaymentMethodForm
  const convertToSavedMethods = (methods: PaymentMethod[]) => {
    return methods.map((method) => ({
      id: method.stripePaymentMethodId,
      brand: method.brand,
      last4: method.last4,
      exp_month: method.expMonth,
      exp_year: method.expYear,
      isDefault: method.isDefault
    }));
  };

  const handleContinueToPayment = async () => {
    try {
      setIsLoading(true);

      // Check if this is coming from UpgradePlan (subscription update) or CreatePlan (new subscription)
      const isSubscriptionUpdate =
        location.state?.customerId &&
        location.state?.proratedAmount !== undefined;

      console.log(
        "🔍 Payment flow type:",
        isSubscriptionUpdate ? "Subscription Update" : "New Subscription"
      );
      console.log("🔍 Location state:", location.state);

      // Get the default/primary payment method
      const defaultPaymentMethod = paymentMethods.find(
        (method) => method.isDefault
      );

      if (!defaultPaymentMethod) {
        Swal.fire({
          icon: "warning",
          title: "No Primary Payment Method",
          text: "Please set a primary payment method before continuing.",
          confirmButtonColor: "#28a745"
        });
        return;
      }

      if (!userToken) {
        Swal.fire({
          icon: "error",
          title: "Authentication Required",
          text: "Please login to continue.",
          confirmButtonColor: "#28a745"
        });
        return;
      }

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`
        }
      };

      let response;

      if (isSubscriptionUpdate) {
        // PATCH /subscription - Update existing subscription
        const subscriptionUpdateData = {
          customerId: location.state.customerId,
          plan: location.state.plan,
          priceId: location.state.priceId,
          maxUsers: Number(location.state.maxUsers)
        };

        console.log(
          "🚀 Making PATCH subscription update API call:",
          subscriptionUpdateData
        );

        response = await axios.patch(
          AppConstants.SUBSCRIPTION_UPDATE_API,
          subscriptionUpdateData,
          config
        );
      } else {
        // POST /subscription - Create new subscription
        const subscriptionData = {
          customerId: planData.customerId, // From user context or planData
          plan: planData.planName, // From CreatePlan component
          priceId: planData.priceId, // From pricing API response
          paymentMethodId: defaultPaymentMethod.stripePaymentMethodId,
          maxUsers: Number(planData.maxUsers), // From CreatePlan selection
          promocode: planData.promocode || "", // From CreatePlan promocode
          autoRenew: !!planData.autoRenew // From CreatePlan auto-renewal setting
        };

        console.log(
          "🚀 Making POST subscription create API call:",
          subscriptionData
        );

        response = await axios.post(
          AppConstants.SUBSCRIPTION_CREATE_API,
          subscriptionData,
          config
        );
      }

      const body = response.data || {};
      const data = body?.data || body || {};

      if (response.status >= 200 && response.status < 300) {
        console.log(
          isSubscriptionUpdate
            ? "✅ Subscription updated successfully!"
            : "✅ Subscription created successfully!"
        );

        if (isSubscriptionUpdate) {
          // Handle subscription update success
          Swal.fire({
            icon: "success",
            title: "Subscription Updated!",
            text: data?.message || "Subscription updated successfully.",
            confirmButtonColor: "#28a745"
          }).then(() => {
            // Navigate back to plan details
            navigate("/plan-details");
          });
        } else {
          // Handle subscription creation (existing logic)
          // Check if we need to confirm payment with Stripe
          const clientSecret =
            data?.clientSecret || data?.latestInvoiceClientSecret;

          if (clientSecret) {
            console.log(
              "🔄 Client secret received, confirming payment with Stripe..."
            );
            console.log("Client secret:", clientSecret);

            // Initialize Stripe and confirm payment
            const stripe = await loadStripe(
              AppConstants.STRIPE_PUBLISHABLE_KEY || ""
            );

            if (!stripe) {
              throw new Error("Stripe failed to initialize");
            }

            const confirmRes = await stripe.confirmCardPayment(clientSecret, {
              // Use an HTTPS return page which will deep-link to the app
              return_url: `${window.location.origin}/stripe/return?flow=sub`
            });

            console.log("Stripe confirmCardPayment result:", confirmRes);

            if (confirmRes.error) {
              console.error(
                "❌ Payment confirmation failed:",
                confirmRes.error
              );
              Swal.fire({
                icon: "error",
                title: "Payment Failed",
                text:
                  confirmRes.error.message || "Payment confirmation failed.",
                confirmButtonColor: "#28a745"
              });
            } else {
              console.log("✅ Payment confirmed successfully!");

              // Navigate to return page for consistent UX even when 3DS2 completes in-place
              const url = new URL("/stripe/return", window.location.origin);
              url.searchParams.set("flow", "sub");
              url.searchParams.set(
                "payment_intent_client_secret",
                clientSecret
              );

              const status = confirmRes.paymentIntent?.status;
              if (status) {
                url.searchParams.set("redirect_status", status);
              }

              console.log("🔄 Redirecting to:", url.toString());
              window.location.assign(url.toString());
              return; // Prevent further local handling
            }
          } else {
            console.log(
              "✅ No payment confirmation needed, subscription created successfully!"
            );

            Swal.fire({
              icon: "success",
              title: "Subscription Created!",
              text: data?.message || "Subscription created successfully.",
              confirmButtonColor: "#28a745"
            }).then(() => {
              // Redirect to mobile app after successful subscription (no payment confirmation needed)
              setTimeout(() => {
                if (
                  window.location.protocol === "https:" ||
                  window.location.hostname === "localhost"
                ) {
                  window.location.href = "pretrip://subscription-success";
                } else {
                  navigate("/subscription/success");
                }
              }, 2000);
            });
          }
        }
      } else {
        throw new Error(
          data?.message ||
            `Failed to ${
              isSubscriptionUpdate ? "update" : "create"
            } subscription.`
        );
      }
    } catch (error: any) {
      const isSubscriptionUpdate =
        location.state?.customerId &&
        location.state?.proratedAmount !== undefined;

      console.error(
        isSubscriptionUpdate
          ? "❌ Error updating subscription:"
          : "❌ Error creating subscription:",
        error
      );
      console.error("Error response:", error?.response?.data);
      console.error("Error status:", error?.response?.status);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        `Failed to ${
          isSubscriptionUpdate ? "update" : "create"
        } subscription. Please try again.`;

      Swal.fire({
        icon: "error",
        title: isSubscriptionUpdate
          ? "Subscription Update Failed"
          : "Subscription Failed",
        text: errorMessage,
        confirmButtonColor: "#28a745"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handlePaymentMethodAdded = async (paymentMethodId?: string) => {
    // Close modal
    setShowAddCardModal(false);

    // Refresh payment methods list
    try {
      await fetchPaymentMethods(setPaymentMethods, userToken);
    } catch (error) {
      console.error("Failed to refresh payment methods:", error);
    }
  };

  const handlePaymentMethodError = (error: string) => {
    console.error("Payment method error:", error);
    // Modal will stay open so user can try again
  };

  const handleRemoveCard = async (id: string) => {
    const methodToRemove = paymentMethods.find((method) => method._id === id);

    Swal.fire({
      title: "Delete Payment Method?",
      text: `Are you sure you want to remove ${methodToRemove?.brand} •••• ${methodToRemove?.last4}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (!methodToRemove) {
            throw new Error("Payment method not found");
          }

          const config = {
            headers: {
              "Content-Type": "application/json",
              ...(userToken && { Authorization: `Bearer ${userToken}` })
            }
          };

          // Make API call to delete payment method
          await axios.delete(
            `${AppConstants.PAYMENTS_DELETE_METHOD_API}/${methodToRemove.stripePaymentMethodId}`,
            config
          );

          // Update local state if API call is successful
          setPaymentMethods(
            paymentMethods.filter((method) => method._id !== id)
          );

          Swal.fire("Deleted!", "Payment method has been removed.", "success");
        } catch (error) {
          console.error("Error deleting payment method:", error);
          Swal.fire(
            "Error!",
            "Failed to delete payment method. Please try again.",
            "error"
          );
        }
      }
    });
  };

  // Show loading state until auth is ready
  if (!ready) {
    return (
      <div className="subscription-dashboard">
        <Header
          title="Payment Methods"
          showMenu={true}
          currentPage="payment-methods"
        />
        <div className="dashboard-body">
          <Container className="px-3 py-4">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Initializing...</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-dashboard">
      <Header
        title="Payment Methods"
        showMenu={true}
        currentPage="payment-methods"
      />

      <div className="dashboard-body">
        <Container className="px-3 py-4">
          <Row className="mb-4 justify-content-md-center">
            <Col xl={10} lg={10} md={12} sm={12}>
              {/* Tabs */}
              <div className="payment-tabs mb-4">
                <Nav
                  variant="pills"
                  className="custom-tabs justify-content-center"
                  style={{
                    backgroundColor: "#f0f0f0",
                    borderRadius: "25px",
                    padding: "4px"
                  }}
                >
                  <Nav.Item className="flex-fill">
                    <Nav.Link
                      active={activeTab === "methods"}
                      onClick={() => setActiveTab("methods")}
                      className="text-center"
                      style={{
                        borderRadius: "20px",
                        fontWeight: "600",
                        border: "none",
                        backgroundColor:
                          activeTab === "methods" ? "white" : "transparent",
                        color: activeTab === "methods" ? "#333" : "#666",
                        boxShadow:
                          activeTab === "methods"
                            ? "0 2px 4px rgba(0,0,0,0.1)"
                            : "none"
                      }}
                    >
                      Payment Methods
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="flex-fill">
                    <Nav.Link
                      active={activeTab === "history"}
                      onClick={() => setActiveTab("history")}
                      className="text-center"
                      style={{
                        borderRadius: "20px",
                        fontWeight: "600",
                        border: "none",
                        backgroundColor:
                          activeTab === "history" ? "white" : "transparent",
                        color: activeTab === "history" ? "#333" : "#666",
                        boxShadow:
                          activeTab === "history"
                            ? "0 2px 4px rgba(0,0,0,0.1)"
                            : "none"
                      }}
                    >
                      Payment History
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              {/* Tab Content */}
              {activeTab === "methods" ? (
                <>
                  {/* Payment Methods List */}
                  <div className="payment-methods-list mb-4">
                    {paymentMethods.map((method) => (
                      <Card
                        key={method._id}
                        className="mb-3"
                        style={{
                          borderRadius: "12px",
                          border: method.isDefault
                            ? "2px solid #28a745"
                            : "1px solid #dee2e6",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                        }}
                      >
                        <Card.Body className="p-3">
                          <Row className="align-items-center">
                            {/* 1. Radio Button (Read-only display) */}
                            <Col xs="auto">
                              <input
                                type="radio"
                                name="primary-card"
                                checked={method.isDefault}
                                readOnly
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  accentColor: "#28a745",
                                  cursor: "default"
                                }}
                              />
                            </Col>

                            {/* 2. Card Icon */}
                            <Col xs="auto">
                              <div
                                className="card-icon"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor:
                                    method.brand === "visa"
                                      ? "#1a1f71"
                                      : "#eb001b",
                                  borderRadius: "8px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white"
                                }}
                              >
                                <FaCreditCard />
                              </div>
                            </Col>

                            {/* 3. Card Details */}
                            <Col>
                              <div>
                                <div className="d-flex align-items-center">
                                  <strong>
                                    {capitalizeBrand(method.brand)} ••••{" "}
                                    {method.last4}
                                  </strong>
                                  {method.isDefault && (
                                    <span className="badge bg-success ms-2">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <small className="text-muted">
                                  Expires{" "}
                                  {formatExpiry(
                                    method.expMonth,
                                    method.expYear
                                  )}
                                </small>
                              </div>
                            </Col>

                            {/* 4. Make Primary Button and Delete Icon */}
                            <Col
                              xs="auto"
                              className="d-flex align-items-center gap-2"
                            >
                              {!method.isDefault && (
                                <Button
                                  variant="link"
                                  className="p-0 text-success fw-bold"
                                  onClick={() => handleSetDefault(method._id)}
                                  style={{
                                    fontSize: "14px",
                                    textDecoration: "none",
                                    fontWeight: "700"
                                  }}
                                >
                                  Make Primary
                                </Button>
                              )}

                              <Button
                                variant="link"
                                className="p-0 text-danger"
                                onClick={() => handleRemoveCard(method._id)}
                                disabled={
                                  method.isDefault &&
                                  paymentMethods.length === 1
                                }
                                style={{ fontSize: "16px" }}
                              >
                                <FaTrashAlt />
                              </Button>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>

                  {/* Add Payment Method Button */}
                  <Button
                    className="w-100 py-3 mb-3"
                    style={{
                      backgroundColor: "#28a745",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "white"
                    }}
                    onClick={() => setShowAddCardModal(true)}
                  >
                    <FaPlus className="me-2" />
                    Add Payment Method
                  </Button>

                  {/* Continue to Payment Button */}
                  <Button
                    className="w-100 py-3"
                    style={{
                      backgroundColor: "#007bff",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "white"
                    }}
                    onClick={handleContinueToPayment}
                    disabled={isLoading}
                  >
                    {isLoading ? "Processing..." : "Continue to Payment"}
                  </Button>
                </>
              ) : (
                <>
                  {/* Payment History List */}
                  <div className="payment-history-list">
                    {paymentHistory.map((payment) => (
                      <Card
                        key={payment.id}
                        className="mb-3"
                        style={{
                          borderRadius: "12px",
                          border: "1px solid #dee2e6",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                        }}
                      >
                        <Card.Body className="p-3">
                          <Row className="align-items-center">
                            <Col xs="auto">
                              <div
                                className="payment-icon"
                                style={{
                                  width: "60px",
                                  height: "70px",
                                  opacity: 1,
                                  borderRadius: "8px",
                                  backgroundColor: "#E7ECF1",
                                  paddingTop: "15px",
                                  paddingRight: "18px",
                                  paddingBottom: "15px",
                                  paddingLeft: "18px",
                                  gap: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                              >
                                <img
                                  src={iconPayment}
                                  alt="Payment Icon"
                                  width={24}
                                  height={24}
                                />
                              </div>
                            </Col>
                            <Col>
                              <div>
                                <small className="text-muted d-block">
                                  Transaction ID:{" "}
                                  {payment.transactionId || "N/A"}
                                </small>

                                <small className="text-muted d-block">
                                  Date:{" "}
                                  {payment.created
                                    ? new Date(payment.created).toLocaleString()
                                    : "-"}
                                </small>
                                <small className="text-muted d-block">
                                  Status:{" "}
                                  <span
                                    style={{
                                      color:
                                        payment.status &&
                                        [
                                          "succeeded",
                                          "succeded",
                                          "paid"
                                        ].includes(payment.status.toLowerCase())
                                          ? "#28a745"
                                          : payment.status &&
                                            ["failure", "failed"].includes(
                                              payment.status.toLowerCase()
                                            )
                                          ? "#dc3545"
                                          : payment.statusColor || "#333"
                                    }}
                                  >
                                    {payment.status
                                      ? payment.status.charAt(0).toUpperCase() +
                                        payment.status.slice(1)
                                      : ""}
                                  </span>
                                </small>
                                <small className="text-muted d-block">
                                  Payment Type:{" "}
                                  {payment.paymentMethod?.brand &&
                                  payment.paymentMethod.brand.toLowerCase() !== "n/a" &&
                                  payment.paymentMethod.last4
                                    ? `${payment.paymentMethod.brand} •••• ${payment.paymentMethod.last4}`
                                    : "N/A"}
                                </small>
                                {payment.maxUsers && (
                                  <small className="text-muted d-block">
                                    Max Users: {payment.maxUsers}
                                  </small>
                                )}
                                {payment.billingPeriod?.start &&
                                  payment.billingPeriod?.end && (
                                    <small className="text-muted d-block">
                                      Period:{" "}
                                      {new Date(
                                        payment.billingPeriod.start
                                      ).toLocaleDateString()}{" "}
                                      -{" "}
                                      {new Date(
                                        payment.billingPeriod.end
                                      ).toLocaleDateString()}
                                    </small>
                                  )}
                              </div>
                            </Col>
                            <Col xs="auto">
                              <div className="text-end">
                                <div
                                  className="fw-bold"
                                  style={{ fontSize: "18px" }}
                                >
                                  ${payment.amount}
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add Card Modal */}
      <Modal
        show={showAddCardModal}
        onHide={() => setShowAddCardModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Payment Method</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Elements stripe={stripePromise}>
            <AddPaymentMethodForm
              onAdded={handlePaymentMethodAdded}
              onError={handlePaymentMethodError}
              onTokenExpired={() => {
                // Handle token expiration - you might want to redirect to login
                setShowAddCardModal(false);
                // navigate('/login');
              }}
              inline={true}
              compact={true}
              existingMethods={convertToSavedMethods(paymentMethods)}
            />
          </Elements>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PaymentMethods;
